/* The sector models (D-66, 11/09 — batch 15): « Votre secteur ? » on day one, and a model that fills
   everything the guide then walks — the window shape, three or four gifts with costs and recommended
   chances, one roue, one three-question quiz. The campaign is the prefilled stepper (step 5) and the
   support is created at activation (step 6) — the model does not need to write them. Models are DATA,
   not code: every value is the merchant's to change. Each gift carries its one-line condition, the walkable set's
   (lib-pack-8, 09/10); a condition never speaks of a review (D-84, `speaksOfReview`).
   The sector kits deferred in D-2 arrive here as content. The first four are Salon (the pilot),
   Restaurant, Café / boulangerie, Autre; the other sectors fall back on « Autre » until their content
   is written (⟦confirmable⟧ with the owner). */
import { currentWorld } from '@/mocks/worlds'
import { apiWrites } from './api'
import { edited, serverLocationId } from './mirror'
import { getAsks, newAskId, newQuestionId, upsertAsk, type Ask } from './asks'
import { getSecteur, setSecteur, setWindowDays, setWorkspace, type SecteurId } from './scope'
import { TENTHS, getLocalTypes, getRoues, guessIcon, newRoueId, newTypeId, setRoues, toReward, upsertType, type GiftCategory, type GiftType, type Roue } from './wheel'

export interface ModelGift { name: string; cost: number; cat: GiftCategory; share: number /* tenths, the four sum to 1000 */; condition: string }
export interface SectorModel {
  id: SecteurId
  /** the window shape, in days (RJ-07's four shapes) */
  windowDays: number
  gifts: ModelGift[]
  roueName: (place: string) => string
  quiz: { name: string; category: string; questions: { text: string; choices: string[]; correct: number }[]; endMessage: string }
}

const salon: SectorModel = {
  id: 'salon', windowDays: 30,
  gifts: [
    { name: 'Un café ou thé offert', cost: 1.5, cat: 'boissons', share: 450, condition: 'À votre prochaine visite.' },
    { name: '–10 % sur la prochaine prestation', cost: 6, cat: 'remises', share: 350, condition: 'Sur une prestation de coiffure.' },
    { name: 'Un soin profond offert', cost: 12, cat: 'beaute', share: 150, condition: 'Sur rendez-vous.' },
    { name: 'Un brushing offert', cost: 25, cat: 'beaute', share: 50, condition: 'Sur rendez-vous.' },
  ],
  roueName: p => `Roue de ${p}`,
  quiz: { name: 'Vos habitudes', category: 'Produits', endMessage: 'Merci pour vos réponses !', questions: [
    { text: 'À quelle fréquence venez-vous chez le coiffeur ?', choices: ['Chaque mois', 'Tous les deux mois', 'Moins souvent'], correct: 0 },
    { text: 'Quelle gamme utilisez-vous chez vous ?', choices: ['Salon', 'Grande surface', 'Bio / naturel'], correct: 0 },
    { text: 'Ce qui compte le plus pour vous ?', choices: ['Le conseil', 'Le résultat', 'Le moment'], correct: 1 },
  ] },
}
const restaurant: SectorModel = {
  id: 'restaurant', windowDays: 7,
  gifts: [
    { name: 'Un café offert', cost: 1, cat: 'boissons', share: 500, condition: 'À la fin de votre prochain repas.' },
    { name: 'Un dessert offert', cost: 4, cat: 'restauration', share: 300, condition: 'Pour un repas à la carte.' },
    { name: '–10 % sur la prochaine addition', cost: 6, cat: 'remises', share: 150, condition: 'Une fois, sur place.' },
    { name: 'Un apéritif pour deux', cost: 12, cat: 'boissons', share: 50, condition: 'Le soir, sur place.' },
  ],
  roueName: p => `Roue de ${p}`,
  quiz: { name: 'Votre repas', category: 'Satisfaction', endMessage: 'Merci pour vos réponses !', questions: [
    { text: 'Le plat était-il à votre goût ?', choices: ['Oui, parfait', 'Bien', 'Peut mieux faire'], correct: 0 },
    { text: 'Le service ?', choices: ['Rapide et attentif', 'Correct', 'Trop lent'], correct: 0 },
    { text: 'Vous reviendriez ?', choices: ['Sans hésiter', 'Peut-être', 'Non'], correct: 0 },
  ] },
}
const cafe: SectorModel = {
  id: 'cafe', windowDays: 1,
  gifts: [
    { name: 'Une viennoiserie offerte', cost: 0.8, cat: 'restauration', share: 500, condition: 'Le matin, sur place.' },
    { name: 'Un café offert', cost: 0.6, cat: 'boissons', share: 350, condition: 'À votre prochaine visite.' },
    { name: '–10 % sur la prochaine commande', cost: 2, cat: 'remises', share: 120, condition: 'Une fois, sur place.' },
    { name: 'Un petit-déjeuner complet', cost: 6, cat: 'restauration', share: 30, condition: 'Le matin, sur place.' },
  ],
  roueName: p => `Roue de ${p}`,
  quiz: { name: 'Votre pause', category: 'Découverte', endMessage: 'Merci pour vos réponses !', questions: [
    { text: 'Vous venez plutôt…', choices: ['Le matin', 'Le midi', 'L’après-midi'], correct: 0 },
    { text: 'Ce que vous prenez le plus souvent ?', choices: ['Un café', 'Une viennoiserie', 'Un déjeuner'], correct: 0 },
    { text: 'Comment nous avez-vous connus ?', choices: ['En passant', 'Bouche à oreille', 'Internet'], correct: 0 },
  ] },
}
const autre: SectorModel = {
  id: 'autre', windowDays: 7,
  gifts: [
    { name: 'Un petit cadeau surprise', cost: 2, cat: 'general', share: 500, condition: 'À votre prochaine visite.' },
    { name: '–10 % sur le prochain achat', cost: 5, cat: 'remises', share: 350, condition: 'Hors promotions.' },
    { name: '–20 % sur le prochain achat', cost: 10, cat: 'remises', share: 150, condition: 'Hors promotions.' },
  ],
  roueName: p => `Roue de ${p}`,
  quiz: { name: 'Mieux vous connaître', category: 'Général', endMessage: 'Merci pour vos réponses !', questions: [
    { text: 'Comment nous avez-vous connus ?', choices: ['En passant', 'Bouche à oreille', 'Internet'], correct: 0 },
    { text: 'C’est votre…', choices: ['Première visite', 'Deuxième ou troisième', 'Vous êtes un habitué'], correct: 0 },
    { text: 'Ce qui compte le plus pour vous ?', choices: ['Le conseil', 'Le prix', 'Le choix'], correct: 0 },
  ] },
}
export const MODELS: Record<SecteurId, SectorModel> = {
  /* an institut takes the salon's gifts; its remise reads the walkable set's institut condition, not « coiffure » */
  salon, institut: { ...salon, id: 'institut', gifts: salon.gifts.map(g => g.cat === 'remises' ? { ...g, condition: 'Hors forfaits.' } : g) }, restaurant, cafe, bar: { ...restaurant, id: 'bar', roueName: p => `Roue de ${p}` },
  garage: { ...autre, id: 'garage', windowDays: 60 }, opticien: { ...autre, id: 'opticien', windowDays: 60 }, boutique: { ...autre, id: 'boutique' }, autre,
}

const appliedKey = () => `bmr.modele.${currentWorld()}`
/** True once a model has been applied in this world — step 1 then never applies it again (« premier jour »). With an API the
    server's lists say it (a blank workspace has no gift and no roue), and nothing is kept in this browser. */
export function modelApplied(): boolean { if (apiWrites) return false; try { return localStorage.getItem(appliedKey()) === '1' } catch { return false } }

/** Apply a sector model to the establishment: the window, the gifts (reused by name, never duplicated), one roue
    named after the place, one quiz. Idempotent — running it twice creates nothing twice. */
export function applyModel(secteur: SecteurId, place: { id: string; name: string }): { gifts: number; roue: Roue; quiz: Ask } {
  const m = MODELS[secteur]
  /* 2b-2 · with an API the server applies it, in one call (POST /v1/app/onboarding/model, 02 §2.4: the same model, ported to
     apps/api src/lib/models.js), and lib/writes reads back the gifts, the roue, the quiz, the window and the sector. What
     this returns until then is the model's own: its number of gifts and the names it gives. */
  if (apiWrites) {
    setWorkspace({ sector: secteur })
    edited({ kind: 'model', sector: secteur, locationId: serverLocationId(place.id) })
    return { gifts: m.gifts.length,
      roue: { id: '', name: m.roueName(place.name), scope: 'location', locations: [serverLocationId(place.id)], usedBy: [], rewards: [] },
      quiz: { id: '', kind: 'quiz', name: m.quiz.name, category: m.quiz.category, endMessage: m.quiz.endMessage, gift: { mode: 'roue' }, createdAt: '', answers: 0, usedBy: [] } }
  }
  setSecteur(secteur)
  setWindowDays(place.id, m.windowDays)
  /* the gifts — into the library, reused by name */
  const lib = getLocalTypes()
  const types: GiftType[] = m.gifts.map(g => {
    const prev = lib.find(t => t.name.trim().toLowerCase() === g.name.toLowerCase())
    const t: GiftType = prev ?? { id: `${newTypeId()}-${Math.random().toString(36).slice(2, 5)}`, name: g.name, internal: g.name, cost: g.cost, validityDays: 60, cap: null, condition: g.condition, status: 'ok', stock: null, icon: guessIcon(g.name), category: g.cat, sameDay: false, links: '' }
    if (!prev) upsertType(t)
    return t
  })
  /* the roue — one, with the model's recommended chances */
  const name = m.roueName(place.name)
  const roues = getRoues()
  let roue = roues.find(w => w.name === name)
  if (!roue) {
    roue = { id: newRoueId(), name, scope: 'location', locations: [place.id], usedBy: [], rewards: types.map((t, i) => ({ ...toReward(t, m.gifts[i].share), weight: m.gifts[i].share })) }
    const sum = roue.rewards.reduce((a, r) => a + r.weight, 0)
    if (sum !== TENTHS) roue.rewards[0].weight += TENTHS - sum
    setRoues([...roues, roue])
  }
  /* the quiz */
  const asks = getAsks()
  let quiz = asks.find(a => a.kind === 'quiz' && a.name === m.quiz.name)
  if (!quiz) {
    quiz = { id: newAskId(), kind: 'quiz', name: m.quiz.name, category: m.quiz.category, questions: m.quiz.questions.map(q => ({ id: newQuestionId(), ...q })), endMessage: m.quiz.endMessage, gift: { mode: 'roue' }, createdAt: new Date().toISOString(), answers: 0, usedBy: [] }
    upsertAsk(quiz)
  }
  try { localStorage.setItem(appliedKey(), '1') } catch { /* fine */ }
  return { gifts: types.length, roue, quiz }
}

export { getSecteur }
